"use client"

import { useState, useEffect, Fragment } from "react"
import { Dialog, Transition } from "@headlessui/react"
import { X, CreditCard, User, Calendar, Pencil, Loader2, AlertCircle } from "lucide-react"
import creditAccountsService from "../../services/creditAccountsService"
import { useAccessChecker } from "../../utils/Functions/Authorization"

const CreditAccountDetailsModal = ({ isOpen, onClose, accountId, onEdit }) => {
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const { checkAccess } = useAccessChecker()
  // Fetch account details when modal opens
  useEffect(() => {
    if (isOpen && accountId) {
      fetchAccountDetails(accountId)
    }
  }, [isOpen, accountId])

  // Fetch account details
  const fetchAccountDetails = async (id) => {
    try {
      setLoading(true)
      const response = await creditAccountsService.get(id)

      if (response.data && response.data.statusCode === 200) {
        setAccount(response.data.data)
      }
    } catch (error) {
      console.error("Error fetching account details:", error)
    } finally {
      setLoading(false)
    }
  }

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A"

    const date = new Date(dateString)
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                {/* Header */}
                <div className="relative border-b border-neutral-200 dark:border-neutral-900 dark:border-neutral-900 dark:bg-neutral-900/30 p-5">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-[400] text-neutral-900 dark:text-white">
                      Account Details
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {loading ? (
                    <div className="flex justify-center items-center h-40">
                      <Loader2 size={30} className="animate-spin text-neutral-600 dark:text-neutral-400" />
                    </div>
                  ) : account ? (
                    <div className="space-y-6">
                      {/* Account Info */}
                      <div className="bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 p-4 rounded-xl border border-neutral-200 ">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="text-md font-[400] text-neutral-900 dark:text-white mb-2">
                              {account.account_name}
                            </h3>
                            <div className="flex items-center text-xs text-neutral-500 dark:text-neutral-400">
                              <CreditCard size={14} className="mr-1" />
                              <span>Credit Reference: {account.account_id}</span>
                            </div>
                          </div>
                          {checkAccess("creditsAccounts", "update") && (

                            <button
                              onClick={() => onEdit(account._id)}
                              className="p-1.5 rounded-full text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Balance */}
                      <div className="border-b border-neutral-200 dark:border-neutral-900  pb-4">
                        <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                          Current Balance
                        </h4>
                        <div className="text-xl font-[500] text-neutral-900 dark:text-white">
                          ${account.balance}{" "}
                          <span className="text-sm text-neutral-500">{account.account_currency}</span>
                        </div>
                      </div>

                      {/* User */}
                      <div className="border-b border-neutral-200 dark:border-neutral-900  pb-4">
                        <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                          Account Holder
                        </h4>
                        <div className="flex items-center">
                          <User size={16} className="mr-2 text-neutral-500" />
                          <span className="text-neutral-900 text-sm dark:text-white">{account.user?.reference || account.user?.fullName}</span>
                        </div>
                        <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{account.user?.email}</p>
                      </div>

                      {/* Status */}
                      <div className="border-b border-neutral-200 dark:border-neutral-900  pb-4">
                        <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                          Account Status
                        </h4>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-[300] ${account.status === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                            : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400"
                            }`}
                        >
                          <span
                            className={`w-2 h-2 mr-1.5 rounded-full ${account.status === "active" ? "bg-green-500" : "bg-red-500"
                              }`}
                          ></span>
                          {account.status?.charAt(0).toUpperCase() + account.status?.slice(1)}
                        </span>
                      </div>

                      {/* Currency */}
                      <div className="border-b border-neutral-200 dark:border-neutral-900  pb-4">
                        <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">Currency</h4>
                        <div className="flex items-center">
                          <span className="text-sm text-neutral-900 dark:text-white">
                            {account.account_currency} (US Dollar)
                          </span>
                        </div>
                      </div>

                      {/* Dates */}
                      <div>
                        <div className="flex items-center mb-2">
                          <Calendar size={14} className="mr-1 text-neutral-500" />
                          <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                            Account Created
                          </span>
                        </div>
                        <p className="text-sm text-neutral-900 dark:text-white mb-1">{formatDate(account.createdAt)}</p>
                        {account.updatedAt && account.updatedAt !== account.createdAt && (
                          <>
                            <div className="flex items-center mb-1 mt-3">
                              <Calendar size={14} className="mr-1 text-neutral-500" />
                              <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">
                                Last Updated
                              </span>
                            </div>
                            <p className="text-sm text-neutral-900 dark:text-white">{formatDate(account.updatedAt)}</p>
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-neutral-500 dark:text-neutral-400">
                      <AlertCircle size={24} className="mx-auto mb-2" />
                      <p>Account not found or has been deleted.</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-4 flex justify-end">
                  <button
                    type="button"
                    className="px-4 py-2 border border-neutral-200 dark:border-neutral-900  rounded-xl text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors text-sm font-[400]"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default CreditAccountDetailsModal
